// Copyright 2026 Hardfin, Inc.
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Children,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useMemo,
  useRef,
} from 'react';

const MAX_LISTBOX_HEIGHT = 400;
const ESTIMATED_ITEM_SIZE = 56;

/**
 * Flattens children passed by MUI Autocomplete into a flat array.
 * When groupBy is used, MUI wraps groups in <li> containing a
 * <ListSubheader> and <ul>. This extracts headers and individual options.
 */
function flattenChildren(children: ReactNode): ReactElement[] {
  const items: ReactElement[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;

    const childProps = child.props as Record<string, unknown>;

    // Option elements have data-option-type; group wrappers don't
    if ('data-option-type' in childProps) {
      items.push(child);
      return;
    }

    // Group wrapper: extract header and option children
    const groupChildren = Children.toArray(childProps.children as ReactNode);

    for (const groupChild of groupChildren) {
      if (!isValidElement(groupChild)) continue;

      if (groupChild.type === 'ul') {
        Children.forEach(
          (groupChild.props as Record<string, unknown>).children as ReactNode,
          (option) => {
            if (isValidElement(option)) {
              items.push(option);
            }
          }
        );
      } else {
        // Group header (ListSubheader)
        items.push(groupChild);
      }
    }
  });

  return items;
}

/**
 * Virtualized listbox for MUI Autocomplete using @tanstack/react-virtual.
 * Measures actual DOM heights at render time — no hardcoded heights needed.
 */
export const VirtualizedListbox = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function VirtualizedListbox(props, ref) {
  const { children, className, ...listboxProps } = props;
  const scrollRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => flattenChildren(children), [children]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ITEM_SIZE,
    overscan: 20,
    paddingStart: 8,
    paddingEnd: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={ref} role="presentation" style={{ minWidth: 360 }}>
      <div
        ref={scrollRef}
        className={className}
        {...listboxProps}
        style={{ maxHeight: MAX_LISTBOX_HEIGHT, overflow: 'auto', padding: 0 }}
      >
        <ul
          style={{
            height: virtualizer.getTotalSize(),
            position: 'relative',
            margin: 0,
            padding: 0,
          }}
        >
          {virtualItems.map((vi) =>
            cloneElement(items[vi.index], {
              key: vi.index,
              ref: virtualizer.measureElement,
              'data-index': vi.index,
              style: {
                position: 'absolute',
                top: vi.start,
                left: 0,
                width: '100%',
              },
            })
          )}
        </ul>
      </div>
    </div>
  );
});
